import React, { useState } from "react";

const PASSWORD_RULES = [
    { test: (p) => p.length >= 8, label: "Mínimo 8 caracteres" },
    { test: (p) => /[a-z]/.test(p), label: "Una letra minúscula" },
    { test: (p) => /[A-Z]/.test(p), label: "Una letra mayúscula" },
    { test: (p) => /[0-9]/.test(p), label: "Un número" },
    { test: (p) => /[^a-zA-Z0-9]/.test(p), label: "Un carácter especial (!@#$%...)" },
];

const isPasswordValid = (password) => PASSWORD_RULES.every((r) => r.test(password));

const PasswordStrength = ({ password }) => {
    if (!password) return null;
    return (
        <ul className="mt-2 space-y-1">
            {PASSWORD_RULES.map((rule, i) => {
                const passes = rule.test(password);
                return (
                    <li key={i} className={`text-xs flex items-center gap-1 ${passes ? "text-green-600" : "text-gray-400"}`}>
                        {passes ? (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2} /></svg>
                        )}
                        {rule.label}
                    </li>
                );
            })}
        </ul>
    );
};

const AuthGate = ({ propertyId, onAuthSuccess, onContinueWithEmail }) => {
    const [view, setView] = useState("choice"); // choice, register, login, email, forgot, resetSent
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Register form state
    const [nombre, setNombre] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // Login form state
    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");

    // Email-only form state
    const [emailOnly, setEmailOnly] = useState("");

    const handleGoogleLogin = () => {
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `${window.REACT_APP_API_ENDPOINT}/auth/google?redirect=${encodeURIComponent(currentPath)}`;
    };

    // Forgot password state
    const [forgotEmail, setForgotEmail] = useState("");

    const handleRegister = async (e) => {
        e.preventDefault();
        setError(null);

        if (!isPasswordValid(password)) {
            setError("La contraseña no cumple todos los requisitos");
            return;
        }

        if (password !== confirmPassword) {
            setError("Las contraseñas no coinciden");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/auth/register/wizard`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ nombre, email, password, propiedadId: propertyId, redirect: window.location.pathname + window.location.search }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Error al crear la cuenta");

            if (data.emailVerificationPending) {
                setView("verifyPending");
                return;
            }

            onAuthSuccess(data.user);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: loginEmail, password: loginPassword, propiedadId: propertyId }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Email o contraseña incorrectos");

            onAuthSuccess(data.user);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEmailOnly = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/auth/continue-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: emailOnly, propiedadId: propertyId }),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Error al procesar la solicitud");

            // Backend no longer returns token — show "check your email" view
            setView("emailSent");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const inputClassName = "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

    if (view === "choice") {
        return (
            <div className="py-8">
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
                    Tu informe está casi listo
                </h2>
                <p className="text-gray-500 text-center mb-8">
                    Para ver el informe de análisis, elige una de las siguientes opciones:
                </p>

                <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                    {/* Option A: Register/Login */}
                    <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Crear cuenta o iniciar sesión</h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Accede a todas las funcionalidades: guardar propiedades, comparar inversiones y más.
                        </p>

                        <button
                            onClick={handleGoogleLogin}
                            className="w-full flex items-center justify-center bg-[#4285F4] hover:bg-[#357ae8] text-white font-medium py-2 px-4 rounded-md shadow-sm mb-3"
                        >
                            <img
                                src="https://developers.google.com/identity/images/g-logo.png"
                                alt="Google"
                                className="h-5 w-5 mr-2 bg-white rounded-full"
                            />
                            Continuar con Google
                        </button>

                        <button
                            onClick={() => { setError(null); setView("register"); }}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md shadow-sm mb-3"
                        >
                            Crear cuenta con email
                        </button>

                        <button
                            onClick={() => { setError(null); setView("login"); }}
                            className="w-full text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                            Ya tengo cuenta
                        </button>
                    </div>

                    {/* Option B: Email only */}
                    <div className="border border-gray-200 rounded-lg p-6 hover:border-green-300 hover:shadow-md transition">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Continuar solo con email</h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Recibe un enlace para acceder al informe sin necesidad de crear una cuenta.
                        </p>

                        <button
                            onClick={() => { setError(null); setView("email"); }}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md shadow-sm"
                        >
                            Recibir por email
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (view === "register") {
        return (
            <div className="py-8 max-w-md mx-auto">
                <button onClick={() => { setError(null); setView("choice"); }} className="text-blue-600 hover:underline mb-4 text-sm">
                    &larr; Volver
                </button>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Crear cuenta</h2>

                {error && <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm">{error}</div>}

                <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                        <input type="text" required value={nombre} onChange={(e) => setNombre(e.target.value)} className={inputClassName} placeholder="Tu nombre" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClassName} placeholder="tu@email.com" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClassName} placeholder="Crea una contraseña segura" />
                        <PasswordStrength password={password} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
                        <input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputClassName} />
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md shadow-sm disabled:opacity-50">
                        {loading ? "Creando cuenta..." : "Crear cuenta"}
                    </button>
                </form>

                <div className="mt-4 text-center">
                    <button onClick={handleGoogleLogin} className="flex items-center justify-center mx-auto bg-[#4285F4] hover:bg-[#357ae8] text-white font-medium py-2 px-4 rounded-md shadow-sm text-sm">
                        <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" className="h-4 w-4 mr-2 bg-white rounded-full" />
                        O continuar con Google
                    </button>
                </div>
            </div>
        );
    }

    if (view === "login") {
        return (
            <div className="py-8 max-w-md mx-auto">
                <button onClick={() => { setError(null); setView("choice"); }} className="text-blue-600 hover:underline mb-4 text-sm">
                    &larr; Volver
                </button>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Iniciar sesión</h2>

                {error && <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm">{error}</div>}

                <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className={inputClassName} placeholder="tu@email.com" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                        <input type="password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className={inputClassName} />
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md shadow-sm disabled:opacity-50">
                        {loading ? "Iniciando sesión..." : "Iniciar sesión"}
                    </button>
                </form>

                <div className="mt-3 text-center">
                    <a href="/login?redirect=" className="text-blue-600 hover:underline text-sm">
                        ¿Olvidaste tu contraseña?
                    </a>
                </div>

                <div className="mt-4 text-center">
                    <button onClick={handleGoogleLogin} className="flex items-center justify-center mx-auto bg-[#4285F4] hover:bg-[#357ae8] text-white font-medium py-2 px-4 rounded-md shadow-sm text-sm">
                        <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" className="h-4 w-4 mr-2 bg-white rounded-full" />
                        O continuar con Google
                    </button>
                </div>

                <p className="text-center text-sm text-gray-500 mt-4">
                    ¿No tienes cuenta?{" "}
                    <button onClick={() => { setError(null); setView("register"); }} className="text-blue-600 hover:underline">
                        Crear cuenta
                    </button>
                </p>
            </div>
        );
    }

    if (view === "email") {
        return (
            <div className="py-8 max-w-md mx-auto">
                <button onClick={() => { setError(null); setView("choice"); }} className="text-blue-600 hover:underline mb-4 text-sm">
                    &larr; Volver
                </button>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Recibir informe por email</h2>
                <p className="text-gray-500 text-sm mb-6">
                    Introduce tu email y te enviaremos un enlace para acceder a tu informe.
                </p>

                {error && <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm">{error}</div>}

                <form onSubmit={handleEmailOnly} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input type="email" required value={emailOnly} onChange={(e) => setEmailOnly(e.target.value)} className={inputClassName} placeholder="tu@email.com" />
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md shadow-sm disabled:opacity-50">
                        {loading ? "Enviando..." : "Enviar enlace"}
                    </button>
                </form>
            </div>
        );
    }

    if (view === "emailSent") {
        return (
            <div className="py-8 max-w-md mx-auto text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
                    <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Revisa tu email</h2>
                <p className="text-gray-500 text-sm mb-2">
                    Hemos enviado un enlace de acceso a <strong>{emailOnly}</strong>.
                </p>
                <p className="text-gray-500 text-sm">
                    Haz clic en el enlace del email para ver tu informe de análisis.
                </p>
            </div>
        );
    }

    if (view === "verifyPending") {
        return (
            <div className="py-8 max-w-md mx-auto text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-4">
                    <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Verifica tu email</h2>
                <p className="text-gray-500 text-sm mb-2">
                    Hemos enviado un enlace de verificación a <strong>{email}</strong>.
                </p>
                <p className="text-gray-500 text-sm">
                    Revisa tu bandeja de entrada y haz clic en el enlace para activar tu cuenta.
                </p>
            </div>
        );
    }

    return null;
};

export default AuthGate;
